import React, { useMemo } from 'react';
import { useError } from 'contexts/error';
import { useQuery } from '@apollo/client/react';
import { Typography, BarChart } from 'components/ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { endOfMonth, startOfMonth, subMonths, format } from 'date-fns';
import { WhiteCard } from 'components/styles_components/card';
import InspectionMonthlyOverviewQuery from 'queries/inspection/monthly_overview';
import Loader from 'components/loader';
import theme from 'theme';

const MonthlyOverview = () => {
  const setError = useError();
  const intl = useIntl();

  const fromDate = useMemo(() => {
    return format(startOfMonth(subMonths(new Date(), 11)), 'yyyy-MM-dd');
  }, []);

  const toDate = useMemo(() => {
    return format(endOfMonth(new Date()), 'yyyy-MM-dd');
  }, []);

  const {
    data = { inspectionMonthlyOverview: { overview: [] } },
    loading,
    error,
  } = useQuery(InspectionMonthlyOverviewQuery, {
    variables: {
      fromDate,
      toDate,
    },
    onError: () => {
      setError('error.inspections.monthly_overview');
    },
  });

  const chartData = useMemo(() => {
    if (!data?.inspectionMonthlyOverview?.overview) {
      return [];
    }

    const overview = data.inspectionMonthlyOverview.overview;

    const monthNames = [
      intl.formatMessage({ id: 'month.january' }),
      intl.formatMessage({ id: 'month.february' }),
      intl.formatMessage({ id: 'month.march' }),
      intl.formatMessage({ id: 'month.april' }),
      intl.formatMessage({ id: 'month.may' }),
      intl.formatMessage({ id: 'month.june' }),
      intl.formatMessage({ id: 'month.july' }),
      intl.formatMessage({ id: 'month.august' }),
      intl.formatMessage({ id: 'month.september' }),
      intl.formatMessage({ id: 'month.october' }),
      intl.formatMessage({ id: 'month.november' }),
      intl.formatMessage({ id: 'month.december' }),
    ];

    return overview.map((item) => ({
      month: monthNames[item.month - 1],
      pending: item.pendingInspections,
      completed: item.completedInspections,
      canceled: item.canceledInspections,
    }));
  }, [data, intl]);

  if (loading || error) {
    return <Loader />;
  }

  return (
    <WhiteCard>
      <Typography variant="h6" gutterBottom>
        <FormattedMessage id="chart.monthly_overview" />
      </Typography>
      {chartData.length === 0 || chartData.every((item) => item.pending === 0 && item.completed === 0 && item.canceled === 0) ? (
        <Typography variant="body1" align="center" sx={{ py: 4 }}>
          <FormattedMessage id="chart.no_data" />
        </Typography>
      ) : (
      <BarChart
        height={400}
        dataset={chartData}
        series={[
          {
            dataKey: 'pending',
            label: intl.formatMessage({ id: 'chart.legend.pending_inspections' }),
            color: theme.palette.chart.pendingInspections,
            stack: 'inspections',
          },
          {
            dataKey: 'completed',
            label: intl.formatMessage({ id: 'chart.legend.completed_inspections' }),
            color: theme.palette.chart.completedInspections,
            stack: 'inspections',
          },
          {
            dataKey: 'canceled',
            label: intl.formatMessage({ id: 'chart.legend.canceled_inspections' }),
            color: theme.palette.chart.canceledInspections,
            stack: 'inspections',
          },
        ]}
        xAxis={[
          {
            dataKey: 'month',

          }
        ]}
        yAxis={[
          {
            label: intl.formatMessage({ id: 'chart.axis.number_of_inspections' }),
            tickMinStep: 1,
          },
        ]}
      />
      )}
    </WhiteCard>
  );
};

export default MonthlyOverview;
